/**
 * Dashboard JS
 *
 * 대시보드 Page에서 사용하는 자바스크립트 모듈입니다.
 *
 * widgetMixin: 위젯 내부 컨텐츠에 High-charts 객체의 Resize 기능을 제공합니다.
 * byteConvertor: 바이트를 값에 따라 단위를 변환하는 기능을 제공합니다.
 * separateByComma: 숫자를 천단위마다 콤마로 구분하여 문자열을 리턴합니다.
 * getCountryNameByCode: 국가코드로 국가명을 리턴합니다.
 *
 * @since 2018-05-15
 * @author ms1124.kim
 */
// 이벤트 버스 유틸
import Vue from 'vue'
// 국가코드 - 이름이 명시되어있는 파일 Import
import * as countryInfo from '~/static/worldmap/countryCode.json'

/*
  Object - dashEventBus
  Description - 대시보드 전역 이벤트 처리용 이벤트 버스 객체
*/
const dashEventBus = new Vue()

/*
  Object - Top5 Color
  설명: 세계 지도에서 사용하는 TOP5 색상
*/
const top5Color = ['#a42aca', '#c441db', '#da70e9', '#e7a5f1', '#f1c3fb']

/*
  Object - Top10 Color
  Description - Top10 차트의 컬러를 정의.
*/
const top10Color = ['#0061CC', '#1883FA', '#18B8FA', '#10EBFF', '#24D6C7', '#417FB4', '#CBA8EB', '#8C80EC', '#A2B7FB', '#619DF6']

/*
  Object - widgetMixin
  Description - 대시보드 위젯에 공통으로 적용되는 Mixin 객체.
*/
const widgetMixin = {
  computed: {
    widgetBasicMenu () {
      return [
        {
          key: 'menu_fullscreen',
          icon: 'fullscreen',
          label: this.$t('dash.dashboardExpandWidget'),
          click: this.onClickExpandWidget
        },
        {
          key: 'menu_delwidget',
          icon: 'delete',
          label: this.$t('dash.dashboardDeleteWidget'),
          click: this.onClickRemoveWidget
        },
        {
          key: 'menu_refresh',
          icon: 'autorenew',
          label: this.$t('dash.dashboardRefreshWidget'),
          click: this.onClickRefreshWidget
        }
      ]
    }
  },
  mounted () {
    // 위젯 메뉴 설정
    this.$emit('setWidgetMenu', this.widgetBasicMenu, this.widgetChartMenu, this.widgetCustomMenu)
    // 최초 마운트 시 위젯 사이즈 지정 (차트 사이즈 설정)
    this.$nextTick(function () {
      this.onResizeContentsFromWidget()
    })
  },
  created () {
    // updateWidgetData 이벤트 등록 - 추이차트, 실시간 차트 제외
    if (this.eventBus) {
      this.eventBus.$on('updateWidgetData', this.getWidgetContentsData)
    }
    // 리사이즈 이벤트 등록
    window.addEventListener('resize', this.onResizeContentsFromWidget)
  },
  beforeDestroy () {
    // 위젯 갱신 이벤트 제거
    if (this.eventBus) {
      this.eventBus.$off('updateWidgetData')
    }
    // 리사이즈 이벤트 제거
    window.removeEventListener('resize', this.onResizeContentsFromWidget)
  },
  methods: {
    /* Chart Type이 여러가지인 위젯인 경우 */
    changePieChart () {
      this.chartType = 'pie'
    },
    changeBarChart () {
      this.chartType = 'bar'
    },
    changeColumnChart () {
      this.chartType = 'column'
    },
    changeTreeMapChart () {
      this.chartType = 'treemap'
    },
    onClickRemoveWidget () {
      this.$emit('removeWidget')
    },
    onClickExpandWidget () {
      this.$emit('expandWidget')
    },
    onClickRefreshWidget () {
      this.refreshChartData()
    },
    onResizeContentsFromWidget () {
      // Chart 래퍼 컨테이너 객체
      const chartWrapper = this.$refs.chartWrapper
      // 래퍼 체크 (임시 코드)
      if (chartWrapper) {
        // 다른 컨텐츠 영역이 있는지 확인
        const otherContents = this.$refs.otherContents
        // 옵션 영역이 있는지 확인
        const chartOption = this.$refs.chartWrapperOpts
        // Chart 인스턴스
        if (this.$refs.chartInstance) {
          const chartInstance = this.$refs.chartInstance
          // Chart 사이즈 조절
          chartInstance.setSize(chartWrapper.clientWidth - ((otherContents) ? otherContents.clientWidth : 0),
            chartWrapper.clientHeight - ((chartOption && this.applyOptionHeight) ? chartOption.clientHeight : 0), false)
        }
      }
    }
  }
}

/*
  Method - byteConvertor
  Description - 바이트 단위를 값에 따라 KB, MG, GB, TB, PB로 변환, Prefix를 붙여 문자열을 리턴.
*/
function byteConvertor (bytes, fixed) {
  if (bytes === 0) {
    return 0
  }
  bytes = parseInt(bytes)
  var s = ['bytes', 'KB', 'MB', 'GB', 'TB', 'PB']
  var e = Math.floor(Math.log(bytes) / Math.log(1024))
  if (e === 0) return '0 ' + s[0]
  else return (bytes / Math.pow(1024, Math.floor(e))).toFixed((fixed === void 0) ? 2 : fixed) + ' ' + s[e]
}

/*
  Method - separateByComma
  Description - 천단위마다 콤마로 구분.
*/
const separateByComma = function (x) {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

/*
  Method - getCountryNameByCode
  Description - 국가코드로 국가명을 리턴.
*/
function getCountryNameByCode (code) {
  return countryInfo[code.toUpperCase()]
}

const decToHexColor = dec => {
  const hex = (dec || '').toString(16)
  const prePad = (target, pad) => {
    if (target.length < pad) {
      target = '0' + target
      return prePad(target, pad)
    } else {
      return target
    }
  }

  return `#${prePad(hex, 6)}`
}

const getDeviceModel = type => {
  try {
    let res = ''
    switch (type) {
      case 0:
        res = 'TAMS'
        break
      case 9:
        res = 'MF2'
        break
      case 10:
        res = 'MFI'
        break
      case 11:
        res = 'MFD'
        break
      case 12:
        res = 'MF2 AE'
        break
      case 20:
        res = 'NGF'
        break
      case 21:
        res = 'IPS'
        break
      case 22:
        res = 'WIPS'
        break
      case 23:
        res = 'ADS'
        break
      default:
        res = ''
        break
    }

    return res
  } catch (e) {
    return ''
  }
}

/**
 * 이름: genUuid
 * 설명: 대시보드 > 위젯 추가 시 고유 ID를 생성하기 위해 사용한다.
 */
function genUuid () {
  function s4 () {
    return ((1 + Math.random()) * 0x10000 | 0).toString(16).substring(1)
  }

  return s4() + s4() + '-' + s4() + '-' + s4() + '-' + s4() + '-' + s4() + s4() + s4()
}
/**
 * --- TAMS 용 ---
 * 이름: getWidgetByName
 * 설명: 전달된 위젯 콘텐츠로 해당하는 위젯 Vue 파일을 동적 생성한다.
 */
function getWidgetByName (widgetList, type) {
  // 해당 타입에 대한 위젯 정보, 위젯 타입 저장 변수
  let widgetInfo, widgetType

  // 위젯 리스트 내, 포함된 타겟의 종류 값 검색
  widgetList.some(widgetTypeList => {
    widgetInfo = widgetTypeList.children.find(widget => {
      return widget.target === type
    })

    if (typeof widgetInfo !== 'undefined') {
      widgetType = widgetTypeList.key
      return true
    } else {
      return false
    }
  })

  // Import 된 객체를 리턴
  if (typeof widgetInfo !== 'undefined') {
    return require(`~/pages/dashboard/dashboard/widgets/${widgetType}/${widgetInfo.target}.vue`).default
  }
}
/**
 * 이름: getWidgetContent
 * 설명: 위젯 ID를 '_'로 분해하여 어떤 콘텐츠의 위젯인지 리턴한다.
 */
function getWidgetContent (widgetId = '') {
  let contents = ''

  if (widgetId) {
    // 위젯 ID 분석
    const splitId = widgetId.split('_') || []
    // 위젯 컴포넌트를 생성
    contents = (splitId.length > 1) ? splitId[1] : ''
  }

  return contents
}

/**
 * 이름: getGeoFillColorByCount
 * 설명: 이벤트 카운터 값을 기준으로 색상을 분할한다.
 */
function getGeoFillColorByCount (divisionTraffic, targetTraffic) {
  let bgColor = ''

  if (divisionTraffic * 5 >= targetTraffic && divisionTraffic * 4 < targetTraffic) {
    bgColor = this.top5Color[0]
  } else if (divisionTraffic * 4 >= targetTraffic && divisionTraffic * 3 < targetTraffic) {
    bgColor = this.top5Color[1]
  } else if (divisionTraffic * 3 >= targetTraffic && divisionTraffic * 2 < targetTraffic) {
    bgColor = this.top5Color[2]
  } else if (divisionTraffic * 2 >= targetTraffic && divisionTraffic < targetTraffic) {
    bgColor = this.top5Color[3]
  } else {
    bgColor = this.top5Color[4]
  }

  return bgColor
}

/**
 * 이름: getFilterDeviceData
 * 설명: FilterDevice 정보를 pick에 설정된 key값으로 리턴한다.
 */
function getFilterDeviceData (items = [], pick = ['device_id', 'device_uname']) {
  return _.map(items, v => _.pick(v, pick))
}

export {
  top5Color,
  top10Color,
  widgetMixin,
  dashEventBus,
  byteConvertor,
  separateByComma,
  getCountryNameByCode,
  decToHexColor,
  getDeviceModel,
  genUuid,
  getWidgetByName,
  getWidgetContent,
  getGeoFillColorByCount,
  getFilterDeviceData
}
